import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './BlockedPage.css';

/**
 * "Query limit reached" wall.
 *
 * Reached when a fact-check returns 429 (quota exhausted). Gated on the Clerk
 * session; also re-checks /usage so a user who still has queries (or landed
 * here by mistake) is sent straight back to the app.
 */
const BlockedPage = () => {
    const navigate = useNavigate();
    const { isLoaded, isSignedIn, getToken } = useAuth();
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        if (!isLoaded) return;
        if (!isSignedIn) {
            navigate('/login');
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const token = await getToken();
                const res = await fetch(`${API_BASE_URL}/usage`, {
                    credentials: 'include',
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (res.ok) {
                    const u = await res.json();
                    if (!cancelled && (u.unlimited || u.queries_remaining > 0)) {
                        navigate('/app');
                        return;
                    }
                }
            } catch {
                // On error, fall through and show the wall.
            }
            if (!cancelled) setChecked(true);
        })();

        return () => { cancelled = true; };
    }, [isLoaded, isSignedIn, getToken, navigate]);

    if (!checked) {
        return <div className="blocked-page-container" />;
    }

    return (
        <div className="blocked-page-container">
            <div className="blocked-card">
                <div className="blocked-icon-wrapper">
                    <AlertCircle size={64} className="blocked-icon" />
                </div>

                <h1>Query limit reached</h1>
                <p className="blocked-message">
                    You've used all your queries for this period. Your allowance resets
                    automatically on a rolling 30-day cycle — thanks for using VerDict AI!
                </p>

                <div className="blocked-action-box">
                    <p>You can head back and pick up right where you left off once your limit resets.</p>
                    <button onClick={() => navigate('/app')} className="blocked-reset-btn">
                        <ArrowLeft size={20} /> Back to App
                    </button>
                </div>

                <div className="blocked-footer">
                    <p>Have feedback? <button onClick={() => navigate('/feedback')} className="text-btn">Send us a note</button></p>
                </div>
            </div>
        </div>
    );
};

export default BlockedPage;
